/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Sources/PipelineOutcomeFailedRunsSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IReportContext } from "Analytics/Shared/Common/Types";
import { ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";
import { IPipelineOutcomeReportConfiguration } from "Pipeline/Analytics/Common/Types";
import { IStageMetrics } from "Pipeline/Analytics/Common/Types";
import { PipelineOutcomeReportSource } from "Pipeline/Analytics/PipelineOutcome/Common/Sources/PipelineOutcomeReportSource";
import { IDailyTaskFailureData, IFailingTaskData, IPipelineOutcomeTaskFailureData } from "Pipeline/Analytics/PipelineOutcome/Common/Types";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Resources";
import { IVssPageContext } from "VSS/Platform/Context";

interface IODataResult {
    BuildCompletedDateSK?: number;
    PipelineRunCompletedDateSK?: number;
    TaskDisplayName: string;
    FailedCount: number;
    PipelineJob: {
        StageName: string;
    };
}

export class PipelineOutcomeFailedRunsSource extends PipelineOutcomeReportSource {
    constructor(pageContext: IVssPageContext) {
        super("PipelineAnalytics_PipelineFailedRuns", pageContext);
    }

    public getStages(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<IStageMetrics[]> {
        return super.getStages(reportContext, confValues, false);
    }

    public queryTaskFailureSummaryAndTrendDataAllStage(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration
    ): Promise<IPipelineOutcomeTaskFailureData[]> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr = super._getTopFailingTaskQueryFilter(reportContext, confValues);
        const groupByStr = `groupby((${this._getPipelineJobStageName()}, ${this._getGroupByTask()}, ${
            PipelineEntities.PipelineRunCompletedDateSK
        }), aggregate(${this._getFailedCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = ` ${PipelineEntities.PipelineRunCompletedDateSK} asc `;
        return this._queryTaskFailureSummaryAndTrendDataForAllStages(queryOptions);
    }

    private async _queryTaskFailureSummaryAndTrendDataForAllStages(queryOptions: ODataQueryOptions): Promise<IPipelineOutcomeTaskFailureData[]> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }
        const pipelineOutcomeTaskFailureDataPerStage: IPipelineOutcomeTaskFailureData[] = [];
        const stageToOrderedDatesMap: { [stage: string]: string[] } = {};
        const stageToTaskTrendMap: { [stage: string]: { [date: string]: IDailyTaskFailureData } } = {};
        const stageToTaskFailuresMap: { [stage: string]: { [task: string]: IFailingTaskData } } = {};
        const stageToFailedCountMap: { [stage: string]: number } = {};
        for (const datum of data.value as IODataResult[]) {
            const dateAsString = DateSKParser.parseDateSKAsDateString(datum.BuildCompletedDateSK || datum.PipelineRunCompletedDateSK);
            const stage = datum.PipelineJob.StageName ? datum.PipelineJob.StageName : Resources.DefaultStageName;
            if (!stageToTaskTrendMap[stage]) {
                stageToTaskTrendMap[stage] = {};
                stageToTaskFailuresMap[stage] = {};
                stageToOrderedDatesMap[stage] = [];
                stageToFailedCountMap[stage] = 0;
            }
            if (!stageToTaskTrendMap[stage][dateAsString]) {
                stageToTaskTrendMap[stage][dateAsString] = {
                    date: dateAsString,
                    taskFailures: []
                };

                stageToOrderedDatesMap[stage].push(dateAsString);
            }

            stageToTaskTrendMap[stage][dateAsString].taskFailures.push({
                taskName: datum.TaskDisplayName,
                failedCount: datum.FailedCount
            });
            if (!stageToTaskFailuresMap[stage][datum.TaskDisplayName]) {
                stageToTaskFailuresMap[stage][datum.TaskDisplayName] = {
                    taskName: datum.TaskDisplayName,
                    failedCount: 0
                };
            }
            stageToTaskFailuresMap[stage][datum.TaskDisplayName].failedCount += datum.FailedCount;
            stageToFailedCountMap[stage] += datum.FailedCount;
        }
        Object.keys(stageToTaskTrendMap).forEach(stage => {
            const stageMetric = this._parseStageData(stage);
            stageMetric.dataCount = stageToFailedCountMap[stage];
            pipelineOutcomeTaskFailureDataPerStage.push({
                stage: stageMetric,
                trendChartData: stageToOrderedDatesMap[stage].map(date => stageToTaskTrendMap[stage][date]),
                topFailingTasks: {
                    failingTasksData: Object.keys(stageToTaskFailuresMap[stage])
                        .map(taskData => {
                            return stageToTaskFailuresMap[stage][taskData];
                        })
                        .sort((a, b) => b.failedCount - a.failedCount),
                    failedCount: stageToFailedCountMap[stage]
                }
            });
        });
        return pipelineOutcomeTaskFailureDataPerStage;
    }
}
